import type { DataAnalysisConfig } from './serviceExecutionApi';

export const initialDataAnalysis: DataAnalysisConfig = { columns: [''], group_column: '', delimiter: ',', decimal: '.', missing_values: ['', 'NA', 'N/A', 'null'], missing_policy: 'REPORT_ONLY', correlations: false };

export function validDataAnalysis(value: DataAnalysisConfig): boolean {
  if (value.group_holm && (!value.group_family_confirmed || !(value.group_comparison || value.multi_group)) || value.group_family_confirmed && !value.group_holm) return false;
  const columns = value.columns.map((item) => item.trim()).filter(Boolean);
  const categorical = (value.categorical_columns || []).map((item) => item.trim()).filter(Boolean);
  const selected = [...columns, ...categorical];
  const group = value.group_column.trim();
  if (!selected.length || selected.length > 12 || selected.some((item) => item.length > 200)
    || new Set(selected).size !== selected.length || selected.includes(group)) return false;
  if (!columns.length && (value.group_comparison || value.multi_group)) return false;
  if (value.correlations && columns.length < 2) return false;
  if (value.reliability) {
    const items = value.reliability.items.map((item) => item.trim());
    const reverse = (value.reliability.reverse_items || []).map((item) => item.trim());
    const minimum = value.reliability.scale_minimum, maximum = value.reliability.scale_maximum;
    if (new Set(reverse).size !== reverse.length || reverse.some((item) => !items.includes(item))) return false;
    if (reverse.length) {
      if (typeof minimum !== 'number' || typeof maximum !== 'number' || !Number.isFinite(minimum) || !Number.isFinite(maximum)
        || Math.abs(minimum) > 1e100 || Math.abs(maximum) > 1e100 || minimum >= maximum) return false;
    } else if ([minimum, maximum].some((item) => item !== null && item !== undefined)) return false;
    if (!value.reliability.coding_confirmed || items.length < 2 || items.length > 12
      || items.some((item) => !columns.includes(item)) || new Set(items).size !== items.length) return false;
  }
  if (value.regression) {
    const outcome = value.regression.outcome.trim();
    const predictors = value.regression.predictors.map((item) => item.trim());
    if (!value.regression.assumptions_confirmed || !columns.includes(outcome)
      || predictors.length < 1 || predictors.length > 5 || new Set(predictors).size !== predictors.length
      || predictors.some((item) => !columns.includes(item) || item === outcome)) return false;
  }
  if (value.multi_group) {
    const groups = value.multi_group.groups.map((item) => item.trim());
    const missing = value.missing_values.map((item) => item.trim().toLocaleLowerCase());
    if (value.multi_group.posthoc && value.multi_group.posthoc !== 'NONE'
      && (groups.length > 12 || columns.length * groups.length * (groups.length - 1) / 2 > 200 || value.multi_group.posthoc !== (value.multi_group.method === 'ANOVA' ? 'WELCH_HOLM' : 'DUNN_HOLM'))) return false;
    if (!group || !value.multi_group.assumptions_confirmed || groups.length < 3 || groups.length > 50
      || new Set(groups).size !== groups.length || groups.some((item) => !item || item.length > 500 || missing.includes(item.toLocaleLowerCase()))) return false;
  }
  if (value.group_comparison) {
    const comparison = value.group_comparison;
    const a = comparison.group_a.trim(), b = comparison.group_b.trim();
    const missing = value.missing_values.map((item) => item.trim().toLocaleLowerCase());
    if (!group || !a || !b || a === b || [a, b].some((item) => missing.includes(item.toLocaleLowerCase()))) return false;
    if (comparison.method === 'PAIRED_T' || comparison.method === 'WILCOXON') {
      const pair = comparison.pair_column?.trim();
      if (!pair || pair === group || columns.includes(pair) || !comparison.paired_samples_confirmed
        || comparison.independent_samples_confirmed) return false;
    } else if (!comparison.independent_samples_confirmed || comparison.paired_samples_confirmed || comparison.pair_column?.trim()) return false;
  }
  return true;
}
